/**
 * 管理后台 API 客户端
 *
 * 三条约定：
 *  1. 所有请求都带管理端令牌（存在 localStorage，见 session.ts）
 *  2. **401 自动清理会话并跳登录页** —— 这是任务 7.9 的核心要求：
 *     手工把令牌改坏后，页面必须跳登录页而不是白屏
 *  3. 403 不跳登录页 —— 身份有效但权限不足，跳登录没有意义，
 *     应该把「无权操作」提示出来
 */
import { clearSession, getToken } from '../stores/session'

/** 后端统一响应外形 */
export interface Ok<T> {
  ok: true
  data: T
}
export interface Fail {
  ok: false
  error: { code: string; message: string }
}
export type Envelope<T> = Ok<T> | Fail

/** 业务错误：带上 code，便于调用方分支 */
export class ApiError extends Error {
  readonly code: string
  readonly status: number
  constructor(code: string, message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
  }
}

/** 认证失效时的回调。由 main.ts 注册（避免 api 层直接依赖 router）。 */
let onUnauthorized: (() => void) | null = null
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn
}

const BASE = '/admin-api'

/**
 * 发一个请求。
 *
 * @param path 以 / 开头的路径，例如 '/auth/login'
 */
export async function request<T>(
  path: string,
  options: { method?: string; body?: unknown } = {}
): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json' }
  const token = getToken()
  if (token) headers.authorization = `Bearer ${token}`

  let res: Response
  try {
    res = await fetch(`${BASE}${path}`, {
      method: options.method ?? 'GET',
      headers,
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {})
    })
  } catch (e) {
    // 网络层失败（服务未启动、断网）—— 与业务错误区分开，否则用户会以为是账号问题
    throw new ApiError('NETWORK_ERROR', '无法连接到服务，请检查网络或稍后重试', 0)
  }

  let body: unknown
  try {
    body = await res.json()
  } catch {
    // 返回体不是 JSON（例如 Nginx 的 502 页面）
    throw new ApiError('BAD_RESPONSE', `服务返回了非预期内容（HTTP ${res.status}）`, res.status)
  }

  const env = body as Envelope<T>

  if (!env || typeof env !== 'object' || !('ok' in env)) {
    throw new ApiError('BAD_RESPONSE', '服务返回格式不正确', res.status)
  }

  if (env.ok) return env.data

  // 认证失效：清会话 + 跳登录。**放在这里而不是各页面里**，
  // 这样任何一个接口撞上过期都能统一处理，不会出现「某个页面忘了处理」
  if (res.status === 401) {
    clearSession()
    onUnauthorized?.()
  }

  throw new ApiError(env.error.code, env.error.message, res.status)
}

/* ---------------- 具体接口 ---------------- */

export interface AdminInfo {
  id: string
  email: string
  displayName: string
  role: string
  status: string
  lastLoginAt: string | null
  createdAt: string
}

export interface LoginData {
  token: string
  expiresAt: string
  admin: AdminInfo
}

export const api = {
  login: (email: string, password: string) =>
    request<LoginData>('/auth/login', { method: 'POST', body: { email, password } }),

  logout: () => request<{ done: boolean }>('/auth/logout', { method: 'POST' }),

  me: () => request<AdminInfo>('/auth/me'),

  changePassword: (oldPassword: string, newPassword: string) =>
    request<{ done: boolean; message: string }>('/auth/change-password', {
      method: 'POST',
      body: { oldPassword, newPassword }
    }),

  listAdmins: () => request<{ items: AdminInfo[] }>('/admins'),

  createAdmin: (input: { email: string; password: string; displayName: string; role: string }) =>
    request<AdminInfo>('/admins', { method: 'POST', body: input }),

  disableAdmin: (id: string) =>
    request<AdminInfo>(`/admins/${id}/disable`, { method: 'POST' }),

  enableAdmin: (id: string) =>
    request<AdminInfo>(`/admins/${id}/enable`, { method: 'POST' }),

  /* ---------------- 审核台（第 8 组） ---------------- */

  queue: (query = '') => request<QueuePage>(`/moderation/queue${query}`),

  pendingTotal: () => request<{ total: number }>('/moderation/pending-total'),

  decide: (input: { targetType: string; targetId: string; action: string; reason?: string }) =>
    request<DecisionResult>('/moderation/decide', { method: 'POST', body: input }),

  reports: (query = '') => request<ReportPage>(`/moderation/reports${query}`),

  reportDetail: (id: string) => request<ReportTicket>(`/moderation/reports/${id}`),

  decideFromReport: (id: string, action: string, note?: string) =>
    request<{ ticket: ReportTicket; affectedReports: number }>(`/moderation/reports/${id}/decide`, {
      method: 'POST',
      body: { action, ...(note ? { note } : {}) }
    }),

  rejectReport: (id: string, note?: string) =>
    request<ReportTicket>(`/moderation/reports/${id}/reject`, {
      method: 'POST',
      body: note ? { note } : {}
    }),

  searchUsers: (q: string) => request<{ items: AdminUserView[] }>(`/moderation/users?q=${encodeURIComponent(q)}`),

  userDetail: (id: string) => request<AdminUserView>(`/moderation/users/${id}`),

  banUser: (id: string, reason: string) =>
    request<AdminUserView>(`/moderation/users/${id}/ban`, { method: 'POST', body: { reason } }),

  unbanUser: (id: string) =>
    request<AdminUserView>(`/moderation/users/${id}/unban`, { method: 'POST' }),

  contentHistory: (type: string, id: string) =>
    request<{ items: AuditEntry[] }>(`/moderation/history/${type}/${id}`),

  recentAudit: () => request<{ items: AuditEntry[] }>('/moderation/audit/recent'),

  /* ---------------- 运营后台（第 13 组） ---------------- */

  metrics: (days = 14) => request<MetricsOverview>(`/operations/metrics?days=${days}`),

  reviewHealth: () => request<ReviewHealth>('/operations/review-health'),

  alerts: () => request<{ items: OpsAlert[] }>('/operations/alerts'),

  auditLogs: (query = '') => request<AuditLogPage>(`/operations/audit-logs${query}`),

  auditActions: () => request<{ items: string[] }>('/operations/audit-actions'),

  topics: () => request<{ items: TopicItem[] }>('/operations/topics'),

  createTopic: (input: { name: string; sortOrder?: number }) =>
    request<TopicItem>('/operations/topics', { method: 'POST', body: input }),

  updateTopic: (id: string, input: { name?: string; sortOrder?: number }) =>
    request<TopicItem>(`/operations/topics/${id}`, { method: 'PATCH', body: input }),

  setTopicActive: (id: string, active: boolean) =>
    request<TopicItem>(`/operations/topics/${id}/active`, { method: 'POST', body: { active } })
}

/* ---------------- 审核台的数据形状（与后端 services/moderation-admin.ts 对应） ---------------- */

export interface QueueItem {
  id: string
  type: 'post' | 'comment'
  excerpt: string
  rules: string[]
  reviewLevel: number
  degraded: boolean
  author: { id: string; nickName: string; avatarUrl: string | null }
  createdAt: string
  waitingMs: number
}

export interface QueuePage {
  items: QueueItem[]
  nextCursor: string | null
  total: number
}

export interface DecisionResult {
  targetType: string
  targetId: string
  action: string
  visibleToPublic: boolean
}

export interface ReportTicket {
  id: string
  targetType: string
  targetId: string
  reason: string
  detail: string | null
  status: string
  reporter: { id: string; nickName: string }
  target: {
    excerpt: string
    visible: boolean
    deleted: boolean
    reviewStatus: string | null
  } | null
  createdAt: string
  handledAt: string | null
  handleNote: string | null
}

export interface ReportPage {
  items: ReportTicket[]
  nextCursor: string | null
}

export interface AdminUserView {
  id: string
  nickName: string
  avatarUrl: string | null
  bio: string
  status: string
  postCount: number
  commentCount: number
  violationCount: number
  createdAt: string
}

export interface AuditEntry {
  id: string
  adminName: string
  action: string
  targetType: string
  targetId: string
  reason: string | null
  createdAt: string
}

/* ---------------- 运营指标的数据形状 ---------------- */

export interface DayPoint {
  date: string
  value: number
}

export interface MetricsOverview {
  totals: { users: number; posts: number; comments: number; interactions: number }
  trends: {
    dau: DayPoint[]
    newUsers: DayPoint[]
    newContent: DayPoint[]
    interactions: DayPoint[]
  }
}

export interface ReviewHealth {
  pendingContent: number
  pendingPosts: number
  pendingComments: number
  pendingReports: number
  approvalRate: number | null
  windowDays: number
}

export interface OpsAlert {
  level: 'warn' | 'critical'
  code: string
  title: string
  detail: string
  value: number
  threshold: number
  link: string
}

export interface AuditLogItem {
  id: string
  adminId: string
  adminName: string
  action: string
  targetType: string
  targetId: string
  reason: string | null
  beforeValue: unknown
  afterValue: unknown
  path: string | null
  createdAt: string
}

export interface AuditLogPage {
  items: AuditLogItem[]
  total: number
  page: number
  limit: number
}

export interface TopicItem {
  id: string
  name: string
  sortOrder: number
  active: boolean
  createdAt: string
}
